import React, { useState } from "react";

import { API, graphqlOperation } from "aws-amplify";
import { updateInboundShipmentMini } from "../../../graphql/inbound/custom-mutations";
import { getInboundShipmentHistory } from "../../../graphql/inbound/custom-queries";

import { Backdrop, CircularProgress } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  Button,
  Confirm,
  useListContext,
  useNotify,
  useRefresh,
} from "react-admin";

import {
  CancelIcon,
  PlacedAtIcon,
  ReleasedAtIcon,
} from "../../../components/common/iconography";
import { logError } from "../../../utils/appUtils";
import { getYardCalendarDaysInclusive } from "../../../utils/dateUtils";
import {
  lambdaUpdateInboundHeader,
  validateReleaseCommitment,
} from "../inboundShipments/InboundUtils";
import {
  InboundState,
  InboundStatus,
  NoteType,
} from "../../../utils/constants";

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
}));

// Maps the active SIT tab to the status its selected shipments advance to.
export const getAdvanceTarget = (tabValue) => {
  if (
    tabValue === InboundStatus.SIT_PENDING ||
    tabValue === InboundStatus.IN_TRANSIT
  ) {
    return {
      status: InboundStatus.SIT_PLACED,
      dateField: "placedDateTime",
      label: "Set Placed",
      verb: "placed",
      icon: <PlacedAtIcon />,
      historyText: "SIT placed.",
    };
  }
  if (
    tabValue === InboundStatus.SIT_PLACED ||
    tabValue === InboundStatus.PLACED
  ) {
    return {
      status: InboundStatus.RELEASED,
      dateField: "releaseDateTime",
      label: "Set Released",
      verb: "released",
      icon: <ReleasedAtIcon />,
      historyText: "SIT released.",
    };
  }
  return null;
};

/**
 * Splits a bulk selection into the ids to advance and the ids held back.
 *
 * A car whose shipment could not be read is `unverified` whichever way it is
 * moving: a release never ran the commitment guard on it, so it fails closed
 * rather than being released on a transient read error, and a placement has
 * no fresh composite-key members for the lambda write. Only a release is
 * guarded beyond that — a car whose inventory an outbound is still waiting on
 * is `blocked`. Both maps carry the railcar number the manager is told about.
 */
export const partitionForAdvance = (
  selectedIds,
  shipmentById,
  targetStatus,
  data,
) => {
  const blockedById = {};
  const unverifiedById = {};
  selectedIds.forEach((id) => {
    const shipment = shipmentById[id];
    if (!shipment) {
      unverifiedById[id] = data?.[id]?.railCarNumber || id;
      return;
    }
    if (
      targetStatus === InboundStatus.RELEASED &&
      validateReleaseCommitment(shipment, targetStatus)
    )
      blockedById[id] =
        shipment.railCarNumber || data?.[id]?.railCarNumber || id;
  });
  const idsToAdvance = selectedIds.filter(
    (id) => !blockedById[id] && !unverifiedById[id],
  );
  return { idsToAdvance, blockedById, unverifiedById };
};

// One clause per reason a car stayed put, so a manager can tell a car that
// needs its outbound fixed from one that only needs the action retried.
const heldBackReasons = (blockedById, unverifiedById) => {
  const cars = (byId) => Object.values(byId).join(", ");
  const reasons = [];
  if (Object.keys(blockedById).length)
    reasons.push(
      `inventory is committed to a pending Outbound on ${cars(blockedById)}`,
    );
  if (Object.keys(unverifiedById).length)
    reasons.push(`${cars(unverifiedById)} could not be read — try again`);
  return reasons.join("; ");
};

// Build the update for one shipment, mirroring the single-car save path
// (DialogSaveButton) so bulk advance keeps state/demurrage/billing in sync
// instead of writing only status + date and leaving the record half-advanced.
// `record` is the fresh read, not the list row.
export const buildAdvanceInput = (target, record, now, nowIso, history) => {
  const input = {
    id: record.id,
    status: target.status,
    [target.dateField]: nowIso,
  };
  // Append the event to the shipment's embedded history, matching the
  // single-car save path. Only when we successfully fetched the current
  // history (Array) — never overwrite it with just this one note.
  if (Array.isArray(history)) {
    input.history = [
      ...history,
      {
        type: NoteType.SHIPMENT,
        text: target.historyText,
        date: nowIso,
        by: window.appUser?.fullName,
      },
    ];
  }
  if (target.status === InboundStatus.SIT_PLACED) {
    input.state = InboundState.PROCESS_SHIPMENT;
    // Ensure a notify date exists so demurrage can be computed at release.
    if (!record.notifyDateTime) input.notifyDateTime = nowIso;
    if (!record.placedDateTime) input.placedDateTime = nowIso;
    if (!record.startDateTime) input.startDateTime = nowIso;
    if (!record.endDateTime) input.endDateTime = nowIso;
  } else if (target.status === InboundStatus.RELEASED) {
    // No remaining-inventory guard here (unlike the single-row AdvanceButton):
    // this list only handles SIT cars, which have no on-hand inventory.
    // Inventory committed to an outbound is guarded, in handleAdvance — the
    // list row carries no commitment fields, so it takes a fetch first.
    input.state = InboundState.COMPLETED;
    input.stripeBillingDateTime = nowIso;
    if (!record.releaseDateTime) input.releaseDateTime = nowIso;
    // Notified through released, both days counted — the same clock the
    // single-car save writes. Storage (placed through pulled) is separate.
    if (record.notifyDateTime) {
      input.daysOnDemurrageClock = getYardCalendarDaysInclusive(
        record.notifyDateTime,
        record.releaseDateTime || nowIso,
      );
    }
  }
  return input;
};

/**
 * Each selected shipment read fresh, with the history an update appends to.
 *
 * The SIT list query omits `history`, so the update would otherwise overwrite
 * it; the same read carries the inventory a release guard needs and the
 * composite-key members a placement writes back. A car missing from
 * `shipmentById` (null) could not be read.
 */
export const readForAdvance = async (ids) => {
  const historyById = {};
  const shipmentById = {};
  await Promise.all(
    ids.map((id) =>
      API.graphql(graphqlOperation(getInboundShipmentHistory, { id }))
        .then((res) => {
          const shipment = res?.data?.getInboundShipment;
          shipmentById[id] = shipment || null;
          historyById[id] = shipment?.history || [];
        })
        .catch((error) => {
          // API.graphql rejects on any error in the result, and one line
          // item whose outbound no longer resolves (deleted, or not readable
          // here) fails its non-null outboundShipment and rejects the whole
          // read, on every attempt. The shipment still comes back, that line
          // item nulled — the guard skips it and still counts
          // quantityCommitted — so only a read with no shipment in it holds
          // the car back. History is left untouched unless it came back
          // whole, rather than risk clobbering it.
          const shipment = error?.data?.getInboundShipment || null;
          shipmentById[id] = shipment;
          historyById[id] = Array.isArray(shipment?.history)
            ? shipment.history
            : null;
          logError("trackCars:BulkAdvanceButton:fetchHistory", error);
        }),
    ),
  );
  return { shipmentById, historyById };
};

/**
 * Write one car's advance. A release is an AppSync update, as it always was.
 * A placement goes through the lambda: placing a car that was never placed,
 * or whose placement a rollback cleared, puts back the TrackCar that rollback
 * took off Track Management — the dispatch form's rule — and only the lambda
 * can do that in the same transaction as the placement. An active car is left
 * alone.
 */
export const advanceShipment = (target, shipment, history, now) => {
  const nowIso = now.toISOString();
  const input = buildAdvanceInput(target, shipment, now, nowIso, history);
  if (target.status !== InboundStatus.SIT_PLACED) {
    return API.graphql(graphqlOperation(updateInboundShipmentMini, { input }));
  }
  return lambdaUpdateInboundHeader(
    shipment,
    { ...input, updatedAt: nowIso },
    { trackCarRestoreId: shipment.placedDateTime ? null : shipment.id },
  );
};

export const BulkAdvanceButton = ({ tabValue }) => {
  const classes = useStyles();
  const notify = useNotify();
  const refresh = useRefresh();
  const { data, selectedIds, onUnselectItems } = useListContext();
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  const target = getAdvanceTarget(tabValue);
  if (!target) return null;

  const handleAdvance = async () => {
    setOpen(false);
    try {
      setSaving(true);
      const now = new Date();

      const { shipmentById, historyById } = await readForAdvance(selectedIds);

      // A car whose inventory an outbound is still waiting on, or whose
      // shipment could not be read, is dropped from the batch rather
      // than failing it — the rest of the selection advances and the manager
      // is told which cars stayed put and why.
      const { idsToAdvance, blockedById, unverifiedById } = partitionForAdvance(
        selectedIds,
        shipmentById,
        target.status,
        data,
      );
      const heldBack = heldBackReasons(blockedById, unverifiedById);

      if (!idsToAdvance.length) {
        notify(`Nothing was ${target.verb} — ${heldBack}.`, "warning");
        return;
      }

      // allSettled so one failed mutation doesn't discard the others.
      const results = await Promise.allSettled(
        idsToAdvance.map((id) =>
          advanceShipment(target, shipmentById[id], historyById[id], now),
        ),
      );

      const succeededIds = idsToAdvance.filter(
        (_, i) => results[i].status === "fulfilled",
      );
      const failedCount = idsToAdvance.length - succeededIds.length;

      if (failedCount) {
        const firstError = results.find((r) => r.status === "rejected")?.reason;
        logError(
          "trackCars:BulkAdvanceButton:handleAdvance",
          firstError ||
            new Error(`${failedCount} shipment(s) failed to update`),
        );
        notify(
          `${succeededIds.length} updated, ${failedCount} failed to update.` +
            (heldBack ? ` Not ${target.verb}: ${heldBack}.` : ""),
          "warning",
        );
      } else if (heldBack) {
        notify(
          `${succeededIds.length} updated. Not ${target.verb}: ${heldBack}.`,
          "warning",
        );
      } else {
        notify(
          `${succeededIds.length} shipment${
            succeededIds.length === 1 ? "" : "s"
          } updated successfully.`,
        );
      }

      onUnselectItems();
      refresh();
    } catch (error) {
      logError("trackCars:BulkAdvanceButton:handleAdvance", error, notify);
    } finally {
      setSaving(false);
    }
  };

  const handleClick = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setOpen(true);
  };

  const count = selectedIds.length;

  return (
    <>
      <Backdrop className={classes.backdrop} open={saving}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Button label={target.label} color="default" onClick={handleClick}>
        {target.icon}
      </Button>
      <Confirm
        isOpen={open}
        title="Advance Shipments"
        content={`Advance ${count} selected shipment${count === 1 ? "" : "s"}?`}
        onConfirm={handleAdvance}
        onClose={() => setOpen(false)}
        CancelIcon={CancelIcon}
      />
    </>
  );
};
